import React, { useState, useEffect, useReducer } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography
} from '@material-ui/core';
import SearchIcon from "@material-ui/icons/Search";
import Lottie from "react-lottie";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import LottieUtils from "../../utils/classes/LottieUtils";
import MaskUtils from "../../utils/classes/MaskUtils";
import toastError from "../../errors/toastError";

import PixLogoImg from "../../assets/miscPng/pix_logo.png";
import NotFoundLottie from "../../assets/lottie/notFound.json";
import SearchLottie from "../../assets/lottie/search.json";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";

const reducer = (state, action) => {
  if (action.type === "LOAD_PIX_MESSAGES") {
    const pixMessages = action.payload;
    const newPixMessages = [];

    pixMessages.forEach(pixMessage => {
      const pixMessageIndex = state.findIndex(pm => pm.id === pixMessage.id);
      if (pixMessageIndex !== -1) state[pixMessageIndex] = pixMessage;
      else newPixMessages.push(pixMessage);
    });

    return [...state, ...newPixMessages];
  }

  if (action.type === "RESET") return [];
};

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexWrap: "wrap", },

  dialogContent: { overflowY: "scroll", ...theme.scrollbarStyles, },

  btnWrapper: { position: "relative" },

  floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  lottieContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "1rem",
  },

  lottieContainerMessage: { fontSize: "1.75rem", fontWeight: "bold", textAlign: "center", },

  pixCard: {
    borderRadius: "20px",
    padding: "10px",
    marginBottom: "5px",
  },

  pixCardActionArea: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    borderRadius: "20px",
    padding: "5px",
  },

  pixNameText: { fontSize: "16px", fontWeight: "bold", },
  pixKeyText: { fontSize: "16px" },
  pixBankText: { fontSize: "14px", color: "#959EAC" },
}));

const PixModal = ({ open, onClose, ticketId }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pixMessages, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [hasMore, setHasMore] = useState(false);

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedPixMessage, setSelectedPixMessage] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState("");



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!open) return;
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [open, searchParam]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchPixMessages = async () => {
        try {
          const { data } = await api.get("/pixMessages", { params: { searchParam, pageNumber } });
          dispatch({ type: "LOAD_PIX_MESSAGES", payload: data.pixMessages });
          setHasMore(data.hasMore);
        }
        catch (exception) {
          console.log("Pix Modal Use-Effect 1 Exception:", exception);
          toastError(exception);
        }
        finally {
          setLoading(false);
        }
      };

      fetchPixMessages();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [open, searchParam, pageNumber]);

  useEffect(() => {
    if (confirmationOpen) return;
    setAdditionalInfo("");
  }, [confirmationOpen]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();

    setTimeout(() => {
      dispatch({ type: "RESET" });
      setPageNumber(1);
      setSearchParam("");
      setLoading(false);
      setHasMore(false);
      setConfirmationOpen(false);
      setSelectedPixMessage(null);
    }, 500);
  };

  const renderPixInfo = (pixMessage, shouldRenderHeader) => {
    return (
      <>
        <Typography gutterBottom component="h3" className={classes.pixNameText}>
          {shouldRenderHeader && `${i18n.t("pixModal.confirmationModal.name")}: `}{pixMessage.name}
        </Typography>

        <Typography gutterBottom component="h3" className={classes.pixKeyText}>
          {shouldRenderHeader && `${i18n.t("pixModal.confirmationModal.key")}: `}{i18n.t(`pixMessagesModal.form.types.${pixMessage.type}`)} - {MaskUtils.maskText(pixMessage.key, pixMessage.type)}
        </Typography>

        <Typography gutterBottom component="h3" className={classes.pixBankText}>
          {shouldRenderHeader && `${i18n.t("pixModal.confirmationModal.bank")}: `}{pixMessage?.bank?.name || pixMessage.bankCustomName}
        </Typography>
      </>
    );
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(previousPageNumber => previousPageNumber + 1);
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
  };

  const handleSelectPixMessage = (pixMessage) => {
    setSelectedPixMessage(pixMessage);
    handleOpenConfirmationModal();
  };

  const handleSendPixMessage = async () => {
    try {
      await api.post(`/messagesPix/${ticketId}`, { pixMessageId: selectedPixMessage.id, additionalInfo });
      handleClose();
    }
    catch (exception) {
      console.log("Handle Send Pix Message Exception:", exception);
      toastError(exception);
    }
  };

  const handleChangeAdditionalInfo = (event) => {
    setAdditionalInfo(event.target.value);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <>
      <ConfirmationModal
        title={i18n.t("pixModal.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={async () => await handleSendPixMessage()}
      >
        {selectedPixMessage && renderPixInfo(selectedPixMessage, true)}
        
        <TextField
          autoFocus
          fullWidth
          label={`${i18n.t("pixModal.confirmationModal.additionalInfo")} ${additionalInfo.length}/${DefaultValuesUtils.TEXT_SIZES.pixAdditionalInfo}`}
          variant="outlined"
          margin="dense"
          multiline
          minRows={5}
          maxRows={5}
          inputProps={{ maxLength: DefaultValuesUtils.TEXT_SIZES.pixAdditionalInfo }}
          value={additionalInfo}
          onChange={event => handleChangeAdditionalInfo(event)}
        />
      </ConfirmationModal>

      <div className={classes.root}>
        <Dialog open={open} onClose={handleClose} maxWidth="xs" scroll="paper" fullWidth>
          <DialogTitle>
            <span>{i18n.t("pixModal.title.up")}</span>

            <br /><br />

            <TextField
              placeholder={i18n.t("pixModal.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              autoFocus
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                )
              }}
            />
          </DialogTitle>

          <DialogContent className={classes.dialogContent} onScroll={handleScroll} dividers>
            {pixMessages.length === 0 && !loading && (
              <div className={classes.lottieContainer}>
                <Lottie options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: NotFoundLottie }} isClickToPauseDisabled={true} />
                <span className={classes.lottieContainerMessage}>{i18n.t("pixModal.messages.noPixFound")}</span>
              </div>
            )}

            {pixMessages.length === 0 && loading && (
              <div className={classes.lottieContainer}>
                <Lottie options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: SearchLottie }} isClickToPauseDisabled={true} />
              </div>
            )}

            {pixMessages.length > 0 && pixMessages.map(pixMessage => (
              <Card
                id={pixMessage.id}
                className={classes.pixCard}
                onClick={() => handleSelectPixMessage(pixMessage)}
              >
                <CardActionArea className={classes.pixCardActionArea}>
                  <Avatar alt={pixMessage.name} src={PixLogoImg} />

                  <CardContent>{renderPixInfo(pixMessage, false)}</CardContent>
                </CardActionArea>
              </Card>
            ))}
          </DialogContent>

          <DialogActions>
            <Button
              type="submit"
              color="inherit"
              variant="outlined"
              className={`${classes.btnWrapper} ${classes.floatingButton}`}
              onClick={handleClose}
            >
              {i18n.t("pixModal.buttons.cancel")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default PixModal;
